import { useState, useEffect, useMemo, useCallback } from 'react';

import { useLazyQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { getRecentActivityQuery } from 'graphql-queries';
import { useMultiLanguage } from 'lane-shared/hooks/useMultiLanguage';

const PER_PAGE = 25;

type RecentActivityFilters = {
  search?: string;
  sortBy?: {
    key: string;
    dir: 'asc' | 'desc';
  };
  startDate?: Date | null;
  endDate?: Date | null;
};

export function useMyRecentActivityQuery(filters?: RecentActivityFilters) {
  const { translate } = useMultiLanguage();
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState({
    start: 0,
    total: 0,
  });

  // Build search parameters based on filters
  const searchParams = useMemo(() => {
    const search: any = {};
    
    if (filters?.sortBy) {
      search.sortBy = {
        key: filters.sortBy.key,
        dir: filters.sortBy.dir,
      };
    }
    
    if (filters?.search) {
      search.content = {
        name: {
          type: 'like',
          value: filters.search,
        },
      };
    }
    
    if (filters?.startDate || filters?.endDate) {
      if (filters.startDate && filters.endDate) {
        // Use date range search on endDate field with overlap type
        // This finds interactions that overlap with the specified date range
        search.endDate = {
          type: 'overlap',
          value: filters.startDate.toISOString(),
          valueB: filters.endDate.toISOString(),
        };
      } else if (filters.startDate) {
        // Use 'gte' type for start date only - filter by startDate
        search.startDate = {
          type: 'gte',
          value: filters.startDate.toISOString(),
        };
      } else if (filters.endDate) {
        // Use 'lte' type for end date only - filter by startDate
        search.startDate = {
          type: 'lte',
          value: filters.endDate.toISOString(),
        };
      }
    }
    
    return search;
  }, [filters]);

  const variables = useMemo(() => ({
    pagination: {
      start: 0,
      perPage: PER_PAGE,
    },
    search: Object.keys(searchParams).length > 0 ? searchParams : undefined,
  }), [searchParams]);

  const [fetchRecentActivity, { data, error, loading, fetchMore }] =
    useLazyQuery(getRecentActivityQuery, {
      client: getClient(),
      fetchPolicy: 'network-only',
    });

  const executeQuery = useCallback(() => {
    fetchRecentActivity({ variables });
    setPage(0); // Reset page when filters change
  }, [variables, fetchRecentActivity]);

  useEffect(() => {
    executeQuery();
  }, [executeQuery]);

  useEffect(() => {
    if (data?.me?.contentInteractions?.items) {
      const items = data.me.contentInteractions.items?.map((item: any) => ({
        ...item,
        contentData: translate({
          model: item.contentData,
          columns: ['name', 'description', 'subtitle'],
        }),
      }));

      setItems(items);
    }

    if (data?.me?.contentInteractions?.pageInfo) {
      setPageInfo(data?.me?.contentInteractions?.pageInfo);
    }
  }, [data?.me?.contentInteractions]);

  useEffect(() => {
    if (page < 1 || loading) {
      return;
    }

    // bug in apollo-client see https://github.com/apollographql/apollo-client/issues/4114#issuecomment-502111099
    try {
      fetchMore({
        variables: {
          pagination: { start: PER_PAGE * page, perPage: PER_PAGE },
          search: Object.keys(searchParams).length > 0 ? searchParams : undefined,
        },
        updateQuery: (prevRes, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevRes;
          }

          if (
            !fetchMoreResult?.me?.contentInteractions?.items ||
            fetchMoreResult.me.contentInteractions.items.length === 0
          ) {
            return prevRes;
          }

          // Ensure prevRes.me and its contentInteractions.items exist before spreading
          if (!prevRes?.me?.contentInteractions?.items) {
            return prevRes;
          }

          return {
            me: {
              __typename: 'Me',
              contentInteractions: {
                __typename: 'UserContentInteractionPagination',
                pageInfo: fetchMoreResult.me.contentInteractions.pageInfo,
                items: [
                  ...prevRes.me.contentInteractions.items,
                  ...fetchMoreResult.me.contentInteractions.items,
                ],
              },
            },
          };
        },
      });
      // FIXME: Log error for datadog, missing stack trace
    } catch {
      // swallow error for now
    }
  }, [page, searchParams]);

  function getNextPage() {
    if (loading) {
      return;
    }

    setPage(prevPage => prevPage + 1);
  }

  return { items, pageInfo, loading, error, getNextPage };
}
